import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Bell } from "react-bootstrap-icons";
import "./header.scss";
import Sidemenu from "@/components/common/sidemenu/sidemenu";
import ProfileDrawer from "@/components/common/profile-drawer/profile-drawer";
import { useAuth0 } from "@auth0/auth0-react";
import { isBoolean, get, isEqual } from "lodash";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "@/assets/icons/WePrice-Secondaryicon-w.png";
import useLocalStorage from "@/hooks/useLocalStorage";
import useCustomParamTracker from "@/hooks/useCustomParamTracker";
import toast from "react-hot-toast";
import { TOAST_MESSAGES } from "@/lang/toast-messages.lang";
import clsx from "clsx";
import UserAvatar from "@/components/common/UserAvatar";
import { useUser } from "@/context/UserContext";
import AnalyticsIcon from "@/assets/icons-v2/analytics.png";
import Help from "@/assets/icons-v2/help_solid.png";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { AppNotification, NotificationsPanel } from "@/components/common/Notification-panel/notifications-panel";
import { getInAppUnreadNotificationCount } from "@/services/NotificationService";
import useNotificationSSE from "@/hooks/useNotificationSSE";
import useModalState from "@/hooks/useModalState";
import useMobileHeaderScrollCollapse from "@/hooks/useMobileHeaderScrollCollapse";

interface ActiveIndex {
    tab: number;
    button: number;
}

interface HeaderProps {}

interface MenuIconProps {
    width?: number;
    height?: number;
    className?: string;
    onClick?: () => void;
    id?: string;
}

const Header: React.FC<HeaderProps> = () => {
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isProfileOpen, setIsProfileOpen] = useState<boolean>(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState<boolean>(false);
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
    const { trackCustomParam } = useCustomParamTracker();
    const { userState, setUserState } = useUser();
    const { setModalId } = useModalState();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const defaultActiveIndex: ActiveIndex = { tab: 0, button: 0 };
    const activeIndex: ActiveIndex = get(userState, "leaderboard_active_tab", defaultActiveIndex);
    const [preferredVersion] = useLocalStorage("live-variant-preferred-version", null);

    const isPuzzleRoute =
        location.pathname === "/" ||
        location.pathname === "/puzzle" ||
        location.pathname.startsWith("/puzzle/");
    /** Puzzle routes always use live-variant header chrome; preferredVersion may lag until UserAvatar syncs localStorage. */
    const isLiveVariant =
        (Boolean(preferredVersion) || isPuzzleRoute) &&
        !location.pathname.includes("classic") &&
        !location.pathname.includes("daily-properties") &&
        !location.pathname.includes("leaderboard") &&
        !location.pathname.includes("social");
    const navigate = useNavigate();

    const { data: unreadCountResponse } = useQuery({
        queryKey: ["in-app-unread-notification-count", userState?.id],
        queryFn: () => getInAppUnreadNotificationCount(String(userState?.id)),
        enabled: Boolean(isAuthenticated && userState?.id && isLiveVariant),
    });
    const unreadBadgeCount = Number(unreadCountResponse?.count ?? 0);
    const isOverlayOpen = isMenuOpen || isProfileOpen || isNotificationsOpen;
    const { isCollapsed, expandHeader } = useMobileHeaderScrollCollapse({
        isEnabled: true,
        isOverlayOpen,
    });

    useNotificationSSE(isAuthenticated && isLiveVariant ? userState?.id : undefined);

    // Use the useCustomParamTracker hook to trigger the handleShowProfileFlag function
    useEffect(() => {
        trackCustomParam("show_profile", handleShowProfileFlag);
    }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    // Since header will always be mounted, we will check router changes and do the necessary updates
    useEffect(() => {
        if (
            !isLoading &&
            isAuthenticated &&
            location.pathname !== "/leaderboard" &&
            !isEqual(activeIndex, defaultActiveIndex)
        ) {
            setUserState(
                {
                    ...userState,
                    leaderboard_active_tab: { tab: 0, button: 0 },
                },
                false,
            );
        }
    }, [location, isLoading, userState]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleMenuToggle = (): void => {
        setIsMenuOpen((prevState) => !prevState);
    };

    /**
     * Toggles the visibility of the user profile, closing the menu if open.
     *
     * @param {boolean|null|object} profileFlag - The desired visibility state of the user profile. If provided, sets the profile visibility accordingly.
     */
    const handleProfileToggle = (profileFlag: boolean | null = null): void => {
        if (isMenuOpen) {
            setIsMenuOpen(false);
        }

        if (isBoolean(profileFlag)) {
            setIsProfileOpen(profileFlag);
        } else {
            setIsProfileOpen((prev) => !prev);
        }
    };

    /**
     * Handles the action of displaying the user profile after a short delay, triggering a login for guests if necessary.
     *
     * @throws {Error} Throws an error if the login redirection fails.
     *
     * @returns {void}
     */
    const handleShowProfileFlag = (): void => {
        if (!isAuthenticated) {
            toast.error(TOAST_MESSAGES.GUEST_PROFILE_ACCESS, { id: "GUEST_PROFILE_ACCESS" });

            setTimeout(
                () =>
                    loginWithRedirect({
                        appState: {
                            toggleProfile: true,
                        },
                    }),
                2000,
            );
        } else {
            setTimeout(() => handleProfileToggle(true), 500);
        }
    };

    /*
    const handleNavigate = (): void => {
        const pathSegments = window.location.pathname.split("/");
        const basePath = pathSegments[1];

        if (basePath.includes("daily-properties")) {
            navigate("/classic");
        } else {
            navigate(`/${basePath}`);
        }
    };
    */

    const handleLeaderboardShortcut = (): void => {

        localStorage.setItem("leaderboardSelectedVersion", "puzzle");
        setUserState(userState, (userState as any)?.is_created, {
            leaderboardSelectedVersion: "puzzle",
        } as any);

        sendMixpanelEvent("Stats Icon Click", {
            isAuthenticated: !!userState?.id,
            pageName: location.pathname,
            pageUrl: window.location.href,
            email: userState?.emailAddress || null,
            nickname: userState?.nickname || null,
            location: "Header - puzzle",
        } as any);

        navigate("/leaderboard");
    };

    return (
        <>
            <header className={clsx(isCollapsed && "header--collapsed-mobile")}>
                <div className={clsx("header-container", isCollapsed && "header-container--mini")}>
                    <div className="menu-icons">
                        <X
                            id="menu-icon"
                            className={clsx("x-icon", !isMenuOpen && "hidden")}
                            size={isCollapsed ? 18 : 30}
                            onClick={handleMenuToggle}
                        />
                        <MenuIcon
                            className={clsx("list-icon", isMenuOpen && "d-none")}
                            width={isCollapsed ? 13 : 18}
                            height={isCollapsed ? 11 : 16}
                            onClick={handleMenuToggle}
                        />
                    </div>

                    <div className="title">
                        <Link
                            to="/"
                            onClick={(event) => {
                                if (isCollapsed) {
                                    event.preventDefault();
                                    expandHeader();
                                    return;
                                }
                                // handleNavigate();
                            }}
                            reloadDocument={!isCollapsed}
                            className="text-decoration-none logo-link"
                            aria-label={isCollapsed ? "Expand header" : "PriceMe home"}
                        >
                            <img className="priceme-logo" src={logo} alt="priceme-logo" />
                            <span className={clsx("priceme-title-text", isCollapsed && "priceme-title-text--mini")}>
                                PriceMe
                            </span>
                        </Link>
                    </div>

                    <span className="tw-flex tw-cursor-pointer tw-gap-2 tw-items-center header-actions">
                        {!isCollapsed && !isAuthenticated && isPuzzleRoute && (
                            <button
                                type="button"
                                className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-bg-transparent tw-border-0 tw-p-0 tw-w-[35px] tw-h-[35px]"
                                aria-label="Open how to play"
                                onClick={() => setModalId("LIVE_VARIANT_HELP_MODAL")}
                            >
                                <img className="tw-w-full tw-h-full" src={Help} alt="" aria-hidden="true" />
                            </button>
                        )}
                        {isCollapsed && !isAuthenticated && isPuzzleRoute && (
                            <button
                                type="button"
                                className="header-mini-help-btn tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-bg-transparent tw-border-0 tw-p-0"
                                aria-label="Open how to play"
                                onClick={() => setModalId("LIVE_VARIANT_HELP_MODAL")}
                            >
                                <img className="tw-w-full tw-h-full" src={Help} alt="" aria-hidden="true" />
                            </button>
                        )}
                        {!isCollapsed && isLiveVariant && (
                            <>
                                {isAuthenticated && (
                                    <div className="tw-relative tw-flex tw-items-center tw-justify-center">
                                        <button
                                            type="button"
                                            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-bg-transparent tw-border-0 tw-p-0"
                                            aria-label="Open notifications"
                                            aria-expanded={isNotificationsOpen}
                                            onClick={() => setIsNotificationsOpen((prev) => !prev)}
                                        >
                                            <Bell className="tw-flex tw-text-white" size={25} aria-hidden="true" />
                                        </button>
                                        {unreadBadgeCount > 0 && (
                                            <div
                                                className="tw-flex tw-items-center tw-justify-center tw-text-white"
                                                style={{
                                                    position: "absolute",
                                                    top: -4,
                                                    right: -5,
                                                    minWidth: 14,
                                                    height: 14,
                                                    borderRadius: 7,
                                                    background: "#f97316",
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    lineHeight: 1,
                                                    padding: "0 3px",
                                                }}
                                            >
                                                {unreadBadgeCount > 99 ? "99+" : unreadBadgeCount}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <img
                                    src={AnalyticsIcon}
                                    alt="View puzzle leaderboard"
                                    className="tw-w-8 tw-h-8 tw-bg-[#3b166a]"
                                    onClick={handleLeaderboardShortcut}
                                />
                            </>
                        )}
                        <UserAvatar onClickProfile={() => setIsProfileOpen((prev) => !prev)} />
                    </span>
                </div>
                <Sidemenu onProfileToggle={handleProfileToggle} isOpen={isMenuOpen} onClose={handleMenuToggle} />
            </header>
            {isProfileOpen && <ProfileDrawer isOpen={isProfileOpen} onClose={handleProfileToggle} />}
            <NotificationsPanel
                open={isNotificationsOpen}
                onClose={() => setIsNotificationsOpen(false)}
                notifications={notifications}
                onNotificationsChange={setNotifications}
            />
        </>
    );
};

const MenuIcon: React.FC<MenuIconProps> = ({ width = 18, height = 16, className, onClick, id }) => {
    return (
        <div className={className} onClick={onClick} id="menu-icon" data-testid="menu-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} fill="none" viewBox="0 0 18 16">
                <path stroke="#fff" strokeLinecap="round" strokeWidth="1.2" d="M1 1h12M1 8h16M1 15h12"></path>
            </svg>
        </div>
    );
};

export default Header;
